'use client';

import { ArrowUpRight, CalendarDays } from 'lucide-react';
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import { formatNumber } from '@/lib/utils';
import { formatDateTime, formatTimeAgo } from '@/lib/utils/date';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import type { UserProfileToolResultData } from '../types';

export interface UserProfileResultCardProperties {
  result: UserProfileToolResultData;
}

export function UserProfileResultCard({ result }: UserProfileResultCardProperties) {
  let joinedAt: Date | null = null;
  if (result.joined) {
    joinedAt = typeof result.joined === 'string' ? new Date(result.joined) : result.joined;
  }
  let verifiedType: UserProfileToolResultData['verifiedType'] | 'blue' | 'none' = 'none';
  if (result.verifiedType) {
    verifiedType = result.verifiedType;
  } else if (result.verified) {
    verifiedType = 'blue';
  }

  const metricCandidates = [
    { label: 'Followers', value: result.followers },
    { label: 'Following', value: result.following },
    { label: 'Tweets', value: result.tweets },
  ];

  const metrics = metricCandidates.filter(
    (metric): metric is { label: string; value: number } => typeof metric.value === 'number' && metric.value >= 0
  );

  const normalizedUsername = stripHandlePrefixes(result.username);
  return (
    <div className="space-y-3 rounded-lg border border-border/70 bg-card/70 p-3 text-sm shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span>{result.name || `@${normalizedUsername}`}</span>
            {verifiedType === 'none' ? null : <VerifiedBadge type={verifiedType} size="sm" />}
          </div>
          <div className="text-xs text-muted-foreground">@{normalizedUsername}</div>
        </div>
        {joinedAt ? (
          <div
            className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            title={formatDateTime(joinedAt)}
          >
            <CalendarDays className="h-3.5 w-3.5" />
            <span>Joined {formatTimeAgo(joinedAt)}</span>
          </div>
        ) : null}
      </div>

      {result.bio ? <div className="whitespace-pre-wrap text-sm text-foreground/90">{result.bio}</div> : null}

      {metrics.length > 0 ? (
        <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
          {metrics.map(({ label, value }) => (
            <div key={label} className="rounded-md border border-border/40 bg-muted/40 px-3 py-2">
              <div className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className="text-sm font-semibold text-foreground">{formatNumber(value)}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="text-xs">
        <a
          href={`https://x.com/${normalizedUsername}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        >
          View profile on X
          <ArrowUpRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
